/**
 * Copyright 2019 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { createStore, applyMiddleware, compose, combineReducers } from 'redux';
import type { Reducer } from 'redux';
import { withExtraArgument } from 'redux-thunk';
import type { AxiosInstance } from 'axios';
import actionTypes from './types/action';
import type { Action, ReduxState, Store } from '@/types';
import { reducers } from './reducers';
import { actionLogger } from './utils/actionLogger';
import { reduxErrorLogger } from './utils/reduxErrorLogger';
import { initialState as ordersDetailsInitialState } from './reducers/orders/details';
import { initialState as ordersAddressDetailsInitialState } from './reducers/orders/addressDetails';
import { isServer } from './utils/isServer';

const combinedReducers = combineReducers(reducers);

const rootReducer: Reducer<ReduxState, Action> = (state, action) => {
  if (action.type === actionTypes.LOG_OUT) {
    const { shelf } = state!;
    state = {
      shelf,
      orders: {
        details: ordersDetailsInitialState,
        addressDetails: ordersAddressDetailsInitialState,
      },
    } as ReduxState;
  }

  return combinedReducers(state, action);
};

let clientStore: Store | null = null;

const configureStore = (axios: AxiosInstance): Store => {
  const middlewares = [withExtraArgument(axios), actionLogger.log(), reduxErrorLogger];
  const composeEnhancers =
    typeof window === 'object' && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
      ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({ trace: true })
      : compose;
  const enhancers = composeEnhancers(applyMiddleware(...middlewares));

  // @ts-expect-error mismatching action types
  const newStore: Store = createStore(rootReducer, {}, enhancers);

  return newStore;
};

export const getOrCreateStore = (axios: AxiosInstance): Store => {
  if (isServer) {
    return configureStore(axios);
  }

  if (!clientStore) {
    clientStore = configureStore(axios);
  }

  return clientStore;
};
